import React, { useState } from "react";
import {
  Container,
  Typography,
  Box,
  useTheme,
  alpha,
  Chip,
  Divider,
  Paper,
  Grid,
  Link,
  Button,
  Switch,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from "@mui/material";

const Cookies = () => {
  const theme = useTheme();
  const [preferencesOpen, setPreferencesOpen] = useState(false);
  const [cookieSettings, setCookieSettings] = useState({
    necessary: true, // always true, cannot be disabled
    functional: true,
    analytics: true,
    marketing: false
  });

  const sections = [
    { id: "what-are-cookies", title: "1. What Are Cookies?" },
    { id: "how-we-use", title: "2. How We Use Cookies" },
    { id: "types-of-cookies", title: "3. Types of Cookies We Use" },
    { id: "third-party", title: "4. Third-Party Cookies" },
    { id: "managing-cookies", title: "5. Managing Cookies" },
    { id: "updates", title: "6. Updates to This Policy" },
    { id: "contact", title: "7. Contact Us" }
  ];

  const handlePreferenceChange = (type) => (event) => {
    setCookieSettings(prev => ({ ...prev, [type]: event.target.checked }));
  };

  const savePreferences = () => {
    // In a real implementation, you would save to localStorage or send to backend
    console.log("Cookie preferences saved:", cookieSettings);
    setPreferencesOpen(false);
    // Optionally show a confirmation message
  };

  const currentDate = new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  return (
    <Container maxWidth="lg" sx={{ py: 8 }}>
      {/* Header */}
      <Box sx={{ textAlign: "center", mb: 6 }}>
        <Chip
          label="Privacy"
          sx={{
            mb: 3,
            background: alpha(theme.palette.primary.main, 0.1),
            color: theme.palette.primary.dark,
            fontWeight: 600,
            fontSize: "0.85rem"
          }}
        />
        <Typography
          variant="h2"
          sx={{
            fontWeight: 700,
            color: theme.palette.text.primary,
            mb: 3,
            fontSize: { xs: "2rem", md: "3rem" }
          }}
        >
          Cookies Policy
        </Typography>
        <Typography
          variant="h6"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "800px",
            mx: "auto",
            lineHeight: 1.6,
            fontSize: "1.1rem"
          }}
        >
          Last updated: {currentDate}
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: theme.palette.text.secondary,
            maxWidth: "700px",
            mx: "auto",
            mt: 2,
            fontSize: "0.95rem"
          }}
        >
          FreshSweeper uses cookies to improve your experience, analyse usage, and personalise content. 
          This policy explains how and why we use them.
        </Typography>
        <Box sx={{ mt: 3 }}>
          <Button
            variant="contained"
            onClick={() => setPreferencesOpen(true)}
            sx={{ borderRadius: 30, px: 4 }}
          >
            Manage Cookies
          </Button>
        </Box>
      </Box>

      <Grid container spacing={4}>
        {/* Sidebar */}
        <Grid item xs={12} md={3}>
          <Paper
            elevation={0}
            sx={{
              position: "sticky",
              top: 100,
              p: 2,
              backgroundColor: alpha(theme.palette.background.default, 0.8),
              borderRadius: 2,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              On this page
            </Typography>
            <Box component="ul" sx={{ pl: 2, m: 0, listStyle: "none" }}>
              {sections.map((section) => (
                <Box component="li" key={section.id} sx={{ mb: 0.5 }}>
                  <Link
                    href={`#${section.id}`}
                    underline="hover"
                    sx={{
                      fontSize: "0.85rem",
                      color: theme.palette.text.secondary,
                      "&:hover": { color: theme.palette.primary.main },
                      textDecoration: "none"
                    }}
                  >
                    {section.title}
                  </Link>
                </Box>
              ))}
            </Box>
          </Paper>
        </Grid>

        {/* Main content */}
        <Grid item xs={12} md={9}>
          <Paper
            elevation={0}
            sx={{
              p: { xs: 2, md: 4 },
              borderRadius: 3,
              backgroundColor: theme.palette.background.paper,
              border: `1px solid ${alpha(theme.palette.divider, 0.1)}`
            }}
          >
            {/* 1. What Are Cookies? */}
            <Box id="what-are-cookies" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                1. What Are Cookies?
              </Typography>
              <Typography variant="body1" paragraph>
                Cookies are small text files that are stored on your device (computer, tablet, or mobile) 
                when you visit a website. They are widely used to make websites work more efficiently, 
                as well as to provide information to the owners of the site.
              </Typography>
              <Typography variant="body1">
                Cookies help us remember your preferences, understand how you interact with our Platform, 
                and improve your overall experience. They do not typically contain any information that 
                personally identifies you, but personal information that we store about you may be linked 
                to the information stored in and obtained from cookies.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 2. How We Use Cookies */}
            <Box id="how-we-use" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                2. How We Use Cookies
              </Typography>
              <Typography variant="body1" paragraph>
                FreshSweeper uses cookies for a variety of purposes, including:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <li><Typography variant="body1"><strong>Essential functionality:</strong> To keep you logged in, remember your session, and enable core features like booking.</Typography></li>
                <li><Typography variant="body1"><strong>Performance and analytics:</strong> To understand how visitors use our Platform, which pages are most popular, and to fix errors.</Typography></li>
                <li><Typography variant="body1"><strong>Preferences:</strong> To remember your language, region, and display settings.</Typography></li>
                <li><Typography variant="body1"><strong>Security:</strong> To protect against fraud, abuse, and to verify your identity.</Typography></li>
                <li><Typography variant="body1"><strong>Marketing (with consent):</strong> To show you relevant ads on other websites and measure campaign effectiveness.</Typography></li>
              </Box>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 3. Types of Cookies We Use */}
            <Box id="types-of-cookies" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                3. Types of Cookies We Use
              </Typography>
              <Typography variant="body1" paragraph>
                We use both session cookies (which expire when you close your browser) and persistent 
                cookies (which remain on your device for a set period or until you delete them). 
                Below are the specific categories:
              </Typography>
              
              <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                Necessary Cookies
              </Typography>
              <Typography variant="body1" paragraph>
                These are essential for the Platform to function. They enable basic features like 
                page navigation, secure login, and booking creation. You cannot opt out of these 
                cookies as the Platform would not work properly without them.
              </Typography>

              <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                Functional Cookies
              </Typography>
              <Typography variant="body1" paragraph>
                These cookies remember your choices (e.g., language, region) and provide enhanced, 
                personalised features. Disabling them may affect some functionality.
              </Typography>

              <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                Analytics/Performance Cookies
              </Typography>
              <Typography variant="body1" paragraph>
                These help us understand how visitors interact with our Platform by collecting and 
                reporting information anonymously. We use tools like Google Analytics to improve 
                our services.
              </Typography>

              <Typography variant="h6" fontWeight={600} sx={{ mt: 2, mb: 1 }}>
                Marketing/Targeting Cookies
              </Typography>
              <Typography variant="body1">
                These cookies track your browsing habits to show you ads that are relevant to you. 
                They also limit the number of times you see an ad and help measure campaign 
                effectiveness. We only place these cookies with your explicit consent.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 4. Third-Party Cookies */}
            <Box id="third-party" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                4. Third-Party Cookies
              </Typography>
              <Typography variant="body1" paragraph>
                Some cookies are placed by third‑party services that appear on our Platform. 
                We use reputable third‑party providers, including:
              </Typography>
              <Box component="ul" sx={{ pl: 2 }}>
                <li><Typography variant="body1"><strong>Google Analytics:</strong> For anonymised usage analytics.</Typography></li>
                <li><Typography variant="body1"><strong>Stripe:</strong> For payment processing (security and fraud prevention).</Typography></li>
                <li><Typography variant="body1"><strong>Send.dm / Twilio:</strong> For SMS and communication services.</Typography></li>
                <li><Typography variant="body1"><strong>Social media platforms (if you choose to share):</strong> e.g., Facebook, Instagram.</Typography></li>
              </Box>
              <Typography variant="body1">
                These third parties may set their own cookies. We do not control those cookies, 
                and you should review the respective privacy policies of those services.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 5. Managing Cookies */}
            <Box id="managing-cookies" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                5. Managing Cookies
              </Typography>
              <Typography variant="body1" paragraph>
                Most web browsers allow you to control cookies through their settings. You can:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <li><Typography variant="body1">Delete all cookies already on your device.</Typography></li>
                <li><Typography variant="body1">Block cookies from being set.</Typography></li>
                <li><Typography variant="body1">Set your browser to alert you when a cookie is being placed.</Typography></li>
              </Box>
              <Typography variant="body1" paragraph>
                Instructions for managing cookies in common browsers:
              </Typography>
              <Box component="ul" sx={{ pl: 2, mb: 2 }}>
                <li><Typography variant="body1"><Link href="https://support.google.com/chrome/answer/95647">Google Chrome</Link></Typography></li>
                <li><Typography variant="body1"><Link href="https://support.mozilla.org/en-US/kb/enable-and-disable-cookies-website-preferences">Mozilla Firefox</Link></Typography></li>
                <li><Typography variant="body1"><Link href="https://support.apple.com/guide/safari/manage-cookies-and-website-data-sfri11471/mac">Safari</Link></Typography></li>
                <li><Typography variant="body1"><Link href="https://support.microsoft.com/en-us/microsoft-edge/delete-cookies-in-microsoft-edge-63947406-40ac-c3b8-1b9a-1f3c5e1e6b2a">Microsoft Edge</Link></Typography></li>
              </Box>
              <Typography variant="body1">
                <strong>Important:</strong> Blocking or deleting necessary cookies may prevent the Platform 
                from working correctly. You can also manage your cookie preferences for FreshSweeper 
                using the “Manage Cookies” button at the top of this page.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 6. Updates to This Policy */}
            <Box id="updates" sx={{ scrollMarginTop: 80, mb: 4 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                6. Updates to This Policy
              </Typography>
              <Typography variant="body1">
                We may update this Cookies Policy from time to time to reflect changes in technology, 
                legal requirements, or our operations. The “Last updated” date at the top of this page 
                indicates when the policy was last revised. We encourage you to review this page 
                periodically for any changes.
              </Typography>
            </Box>

            <Divider sx={{ my: 3 }} />

            {/* 7. Contact Us */}
            <Box id="contact" sx={{ scrollMarginTop: 80, mb: 2 }}>
              <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
                7. Contact Us
              </Typography>
              <Typography variant="body1" paragraph>
                If you have any questions about our use of cookies or this policy, please contact us:
              </Typography>
              <Typography variant="body1" component="div" sx={{ pl: 2 }}>
                <strong>FreshSweeper Inc.</strong><br />
                Email: privacy@freshsweeper.com<br />
                Phone: (973) 380-4757<br />
                {/* Address: 123 Clean Street, Suite 100, San Francisco, CA 94105 */}
              </Typography>
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Cookie Preferences Dialog */}
      <Dialog open={preferencesOpen} onClose={() => setPreferencesOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Cookie Preferences</DialogTitle>
        <DialogContent>
          <Typography variant="body2" paragraph>
            You can choose which cookies to allow. Necessary cookies are required for the Platform to 
            function and cannot be disabled.
          </Typography>
          <Box sx={{ mt: 2 }}>
            <FormControlLabel
              control={<Switch checked={cookieSettings.necessary} disabled />}
              label="Necessary cookies (always active)"
            />
            <Typography variant="caption" display="block" sx={{ mb: 2, color: 'text.secondary' }}>
              Essential for login, bookings, and security.
            </Typography>

            <FormControlLabel
              control={<Switch checked={cookieSettings.functional} onChange={handlePreferenceChange('functional')} />}
              label="Functional cookies"
            />
            <Typography variant="caption" display="block" sx={{ mb: 2, color: 'text.secondary' }}>
              Remember your preferences (language, region).
            </Typography>

            <FormControlLabel
              control={<Switch checked={cookieSettings.analytics} onChange={handlePreferenceChange('analytics')} />}
              label="Analytics cookies"
            />
            <Typography variant="caption" display="block" sx={{ mb: 2, color: 'text.secondary' }}>
              Help us improve the Platform with anonymous usage data.
            </Typography>

            <FormControlLabel
              control={<Switch checked={cookieSettings.marketing} onChange={handlePreferenceChange('marketing')} />}
              label="Marketing cookies"
            />
            <Typography variant="caption" display="block" sx={{ color: 'text.secondary' }}>
              Show relevant ads and measure campaign performance.
            </Typography>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setPreferencesOpen(false)}>Cancel</Button>
          <Button onClick={savePreferences} variant="contained">Save Preferences</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Cookies;